import React, { useState, useEffect, useCallback } from "react";
import { Form } from "react-bootstrap";
import { NumericFormat } from "react-number-format";
import stateCity from "../../../utils/state_city.json";
import deletebtn from "../../../images/delete4.png";
import addbtn from "../../../images/plus4.png";
import "./OfficersIncExc.scss";

const ALL_US_STATES = Array.from(
  new Set((stateCity || []).map((item) => item.Code).filter(Boolean))
).sort();

const defaultInitialOfficer = {
  state: "",
  locNumber: "",
  name: "",
  ownership: "",
  duties: "Executive",
  inclusionType: "",
  classCode: "",
  payroll: "",
  stateError: "",
  classCodeError: "",
};

export const isOfficerEmpty = (off) => {
  if (!off) return true;
  const fields = ["state", "locNumber", "name", "ownership", "inclusionType", "classCode", "payroll"];
  return fields.every((k) => !String(off[k] ?? "").trim());
};

const OfficersIncExc = ({
  officersList: initialOfficersList,
  childrenLoc = {},
  updateOfficers,
  disabled = false,
}) => {
  const [officersList, setOfficersList] = useState(() => {
    if (initialOfficersList && initialOfficersList?.length > 0) {
      return initialOfficersList?.map((off) => ({
        state: off?.state || "",
        locNumber: off?.locNumber !== undefined ? String(off?.locNumber) : "",
        name: off?.name || "",
        ownership: off?.ownership !== undefined ? String(off?.ownership) : "",
        duties: off?.duties !== undefined ? off?.duties : "Executive",
        inclusionType: off?.inclusionType || "",
        classCode: off?.classCode || "",
        payroll: off?.payroll !== undefined ? String(off?.payroll) : "",
        stateError: "",
        classCodeError: "",
      }));
    }
    return [{ ...defaultInitialOfficer }];
  });

  // Notify parent with clean fields
  const notifyParent = useCallback(
    (updatedList) => {
      setOfficersList(updatedList);

      if (updateOfficers) {
        const cleanList = updatedList?.map((officer) => ({
          state: officer?.state || "",
          locNumber: officer?.locNumber || "",
          name: officer?.name || "",
          ownership: officer?.ownership || "",
          duties: officer?.duties !== undefined ? officer?.duties : "Executive",
          inclusionType: officer?.inclusionType || "",
          classCode: officer?.classCode || "",
          payroll: officer?.payroll || "",
        }));
        updateOfficers(cleanList, true);
      }
    },
    [updateOfficers]
  );

  // Sync when props.officersList changes
  useEffect(() => {
    if (initialOfficersList && initialOfficersList?.length > 0) {
      setOfficersList((prev) => {
        const incomingStr = JSON.stringify(
          initialOfficersList?.map((off) => ({
            state: off?.state || "",
            locNumber: off?.locNumber !== undefined ? String(off?.locNumber) : "",
            name: off?.name || "",
            ownership: off?.ownership !== undefined ? String(off?.ownership) : "",
            duties: off?.duties !== undefined ? off?.duties : "Executive",
            inclusionType: off?.inclusionType || "",
            classCode: off?.classCode || "",
            payroll: off?.payroll !== undefined ? String(off?.payroll) : "",
          }))
        );
        const currentStr = JSON.stringify(
          prev?.map((off) => ({
            state: off?.state || "",
            locNumber: off?.locNumber || "",
            name: off?.name || "",
            ownership: off?.ownership !== undefined ? String(off?.ownership) : "",
            duties: off?.duties !== undefined ? off?.duties : "Executive",
            inclusionType: off?.inclusionType || "",
            classCode: off?.classCode || "",
            payroll: off?.payroll !== undefined ? String(off?.payroll) : "",
          }))
        );
        if (incomingStr !== currentStr) {
          return initialOfficersList?.map((off) => ({
            state: off?.state || "",
            locNumber: off?.locNumber !== undefined ? String(off?.locNumber) : "",
            name: off?.name || "",
            ownership: off?.ownership !== undefined ? String(off?.ownership) : "",
            duties: off?.duties !== undefined ? off?.duties : "Executive",
            inclusionType: off?.inclusionType || "",
            classCode: off?.classCode || "",
            payroll: off?.payroll !== undefined ? String(off?.payroll) : "",
            stateError: "",
            classCodeError: "",
          }));
        }
        return prev;
      });
    } else if (initialOfficersList && initialOfficersList?.length === 0) {
      setOfficersList((prev) => {
        if (prev?.length === 1 && isOfficerEmpty(prev[0])) {
          return prev;
        }
        return [{ ...defaultInitialOfficer }];
      });
    }
  }, [initialOfficersList]);

  // Handlers
  const handleAddOfficer = () => {
    const updatedList = [...officersList, { ...defaultInitialOfficer }];
    notifyParent(updatedList);
  };

  const handleRemoveOfficer = (index) => {
    const updatedList = officersList.filter((_, i) => i !== index);
    notifyParent(updatedList);
  };

  const handleFieldChange = (index, field, value) => {
    const updatedList = [...officersList];
    updatedList[index] = {
      ...updatedList[index],
      [field]: value,
      ...(field === "state" ? { stateError: "" } : {}),
      ...(field === "classCode" ? { classCodeError: "" } : {}),
    };
    notifyParent(updatedList);
  };

  return (
    <div className="officers-container">
      <div className="pink-header sub_headings">
        <div className="font-family-montserrat">
          Individuals Included/Excluded
        </div>
      </div>

      <div className="officers-rows-wrapper">
        {officersList.map((officer, index) => (
          <div key={index} className="officer-row">
            <div className="row grey loc1street_margin align-items-start no-gutters">
              {/* 0. Row Label (Officer - 1, Officer - 2, ...) */}
              <div className="officer-label-col">
                <Form.Group className="form-group mb-0">
                  <label className="font-family-montserrat-semi-bold officer-label-placeholder">
                    &nbsp;
                  </label>
                  <div className="officer-label-text">
                    <p>Officer - {index + 1}</p>
                  </div>
                </Form.Group>
              </div>

              {/* 1. State Dropdown */}
              <div className="officer-state-col">
                <Form.Group className="form-group mb-0">
                  <label className="font-family-montserrat-semi-bold">
                    State
                  </label>
                  <select
                    disabled={disabled}
                    className={`rectangle1 ${
                      officer?.state
                        ? "officer-select-selected"
                        : "officer-select-placeholder"
                    } ${officer?.stateError ? "officer-input-error" : ""}`}
                    value={officer?.state || ""}
                    onChange={(e) =>
                      handleFieldChange(index, "state", e.target.value)
                    }
                  >
                    <option value="" className="officer-option-default">
                      Select
                    </option>
                    {ALL_US_STATES.map((st) => (
                      <option
                        key={st}
                        value={st}
                        className="officer-option-item"
                      >
                        {st}
                      </option>
                    ))}
                  </select>
                  {officer?.stateError && (
                    <p className="errMsg officer-error-msg">
                      {officer?.stateError}
                    </p>
                  )}
                </Form.Group>
              </div>

              {/* 2. Loc# Input Field */}
              <div className="officer-loc-col">
                <Form.Group className="form-group mb-0">
                  <label className="font-family-montserrat-semi-bold">
                    Loc#
                  </label>
                  <NumericFormat
                    disabled={disabled}
                    className="rectangle1"
                    placeholder="Loc#"
                    maxLength={3}
                    decimalScale={0}
                    allowNegative={false}
                    value={officer?.locNumber || ""}
                    onValueChange={(values) =>
                      handleFieldChange(index, "locNumber", values.value)
                    }
                    isAllowed={(values) => {
                      const { value } = values;
                      return value === "" || (/^\d+$/.test(value) && value.length <= 3);
                    }}
                    autoComplete="off"
                  />
                </Form.Group>
              </div>

              {/* 3. Name Input Field */}
              <div className="officer-name-col">
                <Form.Group className="form-group mb-0">
                  <label className="font-family-montserrat-semi-bold">
                    Name
                  </label>
                  <input
                    type="text"
                    disabled={disabled}
                    className="rectangle1"
                    placeholder="Name"
                    value={officer?.name || ""}
                    onChange={(e) =>
                      handleFieldChange(index, "name", e.target.value)
                    }
                    autoComplete="off"
                  />
                </Form.Group>
              </div>

              {/* 4. Ownership % Input Field */}
              <div className="officer-ownership-col">
                <Form.Group className="form-group mb-0">
                  <label className="font-family-montserrat-semi-bold">
                    Ownership
                  </label>
                  <NumericFormat
                    disabled={disabled}
                    className="rectangle1"
                    placeholder="Enter Value"
                    suffix="%"
                    value={officer?.ownership || ""}
                    onValueChange={(values) =>
                      handleFieldChange(index, "ownership", values.value)
                    }
                    isAllowed={(values) => {
                      const { floatValue } = values;
                      return (
                        floatValue === undefined ||
                        (floatValue >= 0 && floatValue <= 100)
                      );
                    }}
                    autoComplete="off"
                  />
                </Form.Group>
              </div>

              {/* 5. Duties Input Field */}
              <div className="officer-duties-col">
                <Form.Group className="form-group mb-0">
                  <label className="font-family-montserrat-semi-bold">
                    Duties
                  </label>
                  <input
                    type="text"
                    disabled={disabled}
                    className="rectangle1"
                    placeholder="Duties"
                    value={officer?.duties !== undefined ? officer?.duties : "Executive"}
                    onChange={(e) =>
                      handleFieldChange(index, "duties", e.target.value)
                    }
                    autoComplete="off"
                  />
                </Form.Group>
              </div>

              {/* 6. Included / Excluded Dropdown */}
              <div className="officer-inclusion-col">
                <Form.Group className="form-group mb-0">
                  <label className="font-family-montserrat-semi-bold">
                    Included/Excluded
                  </label>
                  <select
                    disabled={disabled}
                    className={`rectangle1 ${
                      officer?.inclusionType
                        ? "officer-select-selected"
                        : "officer-select-placeholder"
                    }`}
                    value={officer?.inclusionType || ""}
                    onChange={(e) =>
                      handleFieldChange(index, "inclusionType", e.target.value)
                    }
                  >
                    <option value="" className="officer-option-default">
                      Select
                    </option>
                    <option value="INCLUDED" className="officer-option-item">
                      Included
                    </option>
                    <option value="EXCLUDED" className="officer-option-item">
                      Excluded
                    </option>
                  </select>
                </Form.Group>
              </div>

              {/* 7. Class Code Input Field */}
              <div className="officer-classcode-col">
                <Form.Group className="form-group mb-0">
                  <label className="font-family-montserrat-semi-bold">
                    Class Code
                  </label>
                  <input
                    type="text"
                    disabled={disabled}
                    className={`rectangle1 ${
                      officer?.classCodeError ? "officer-input-error" : ""
                    }`}
                    placeholder="Class Code"
                    value={officer?.classCode || ""}
                    onChange={(e) =>
                      handleFieldChange(index, "classCode", e.target.value)
                    }
                    autoComplete="off"
                  />
                  {officer?.classCodeError && (
                    <p className="errMsg officer-error-msg">
                      {officer?.classCodeError}
                    </p>
                  )}
                </Form.Group>
              </div>

              {/* 8. Payroll Input Field */}
              <div className="officer-payroll-col">
                <Form.Group className="form-group mb-0">
                  <label className="font-family-montserrat-semi-bold">
                    Payroll
                  </label>
                  <NumericFormat
                    disabled={disabled}
                    className="rectangle1"
                    placeholder="Payroll"
                    prefix="$ "
                    thousandSeparator=","
                    decimalScale={0}
                    allowNegative={false}
                    value={officer?.payroll || ""}
                    onValueChange={(values) =>
                      handleFieldChange(index, "payroll", values.value)
                    }
                    isAllowed={(values) => {
                      const { value } = values;
                      return value === "" || /^\d+$/.test(value);
                    }}
                    autoComplete="off"
                  />
                </Form.Group>
              </div>

              {/* 9. Delete Action */}
              <div className="officer-action-col">
                <Form.Group className="form-group mb-0">
                  <label className="font-family-montserrat-semi-bold officer-label-placeholder">
                    &nbsp;
                  </label>
                  <div className="officer-action-btn-wrapper">
                    {officersList.length > 1 && (
                      <button
                        type="button"
                        disabled={disabled}
                        className="btnInvisible transpButton"
                        onClick={() => handleRemoveOfficer(index)}
                        title="Remove Officer"
                      >
                        <img
                          src={deletebtn}
                          alt="Delete"
                          className="delete_records_btn"
                        />
                      </button>
                    )}
                  </div>
                </Form.Group>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add Officer Button */}
      <div className="officer-add-btn-wrapper">
        <button
          disabled={disabled}
          type="button"
          className="btnInvisible addLoc transpButton p-0"
          onClick={handleAddOfficer}
        >
          <img src={addbtn} alt="Add" /> <b>Add Officer</b>
        </button>
      </div>
    </div>
  );
};

export default OfficersIncExc;
